import { useTranslation } from 'react-i18next';
import { tarabeezaMarkDataUri } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { useResolvedTheme } from '@/Hooks/useTheme';

/**
 * The Tarabeeza mark — ترابيزة, "table" — drawn once in `@/lib/ui-tokens`
 * and rendered here.
 *
 * The token exports an SVG *string* rather than a component, because React
 * Native cannot render `<svg>` and the mobile apps consume the same definition.
 * A `data:` URI on an `<img>` is the honest way to take a string into the DOM:
 * no `dangerouslySetInnerHTML`, and the browser caches identical URIs, so the
 * sidebar and the header cost one decode between them.
 *
 * Two things the artwork decides for us and this component must not fight:
 *
 * - The tabletop ring is dropped below 48px by `tarabeezaMark` itself. Its
 *   stroke is 0.8 at the authored size, which lands at 0.3px on a 32px mark and
 *   renders as a smear rather than a ring. `size` is passed through so that
 *   guard can fire; do not pass `jewel` to defeat it.
 * - There is a `light` variant, with a cream tile and a dark table, for light
 *   grounds. Putting the dark tile on the cream page would make the mark a
 *   black hole in the corner, so the variant follows the *resolved* theme —
 *   which is why this reads `useResolvedTheme()` and not the preference.
 */
export function BrandMark({
  size = 32,
  className,
  radius,
}: {
  size?: number;
  className?: string;
  radius?: number;
}) {
  const theme = useResolvedTheme();
  const src = tarabeezaMarkDataUri({
    size,
    light: theme === 'light',
    ...(radius === undefined ? {} : { radius }),
  });

  return (
    <img
      src={src}
      width={size}
      height={size}
      alt=""
      aria-hidden
      className={cn('shrink-0 select-none', className)}
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

/**
 * Mark plus wordmark, for the two places the product says its own name: the
 * sidebar head and the login card. The Arabic name carries the pronunciation
 * that the Latin one only approximates, so both are shown.
 */
export function BrandLockup({
  size = 32,
  showName = true,
  className,
  nameClassName,
}: {
  size?: number;
  showName?: boolean;
  className?: string;
  /**
   * Classes on the wordmark itself, so a caller can hide it at one breakpoint
   * and keep it at another. The collapsed sidebar rail needs exactly that: the
   * name goes on a desktop icon rail but stays in the phone drawer, which is
   * always full width.
   */
  nameClassName?: string;
}) {
  const { t, i18n } = useTranslation();
  const arabic = i18n.language.startsWith('ar');

  return (
    <span className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <BrandMark size={size} />
      {showName && (
        <span className={cn('flex min-w-0 flex-col leading-tight', nameClassName)}>
          {/* In Arabic this span holds the Arabic name, and `tracking-tight`
              would put -0.025em between letters that are supposed to join —
              the same defect AuthShell's heading carries a fix for. */}
          <span className="truncate font-semibold tracking-tight rtl:tracking-normal">
            {t('brand.name')}
          </span>
          {!arabic && (
            <span className="truncate font-arabic text-xs text-ink-muted" dir="rtl" lang="ar">
              {t('brand.nameArabic')}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
